import { useEffect, useRef, useState, type FormEvent } from 'react';
import { LIMITS } from '@elixir/shared';
import { navigate } from '../App';
import { api, ApiError, sessions, type SavedSession } from '../state/api';
import { VoiceSession } from '../voice/VoiceSession';
import { HeadphonesIcon } from './icons';
import { LevelBar } from './LevelBar';
import { RoomView } from './RoomView';

type Step =
  | { kind: 'loading' }
  | { kind: 'missing'; message: string }
  | { kind: 'name' }
  | { kind: 'check'; session: SavedSession }
  | { kind: 'room'; session: SavedSession; voice: VoiceSession };

const PREFS = 'elixir.audioPrefs';
const loadPrefs = (): { inputId?: string; outputId?: string } => {
  try {
    return JSON.parse(localStorage.getItem(PREFS) ?? '{}');
  } catch {
    return {};
  }
};

/** Works out who you are for this quiz (saved session, host link, or a new name), then runs the mic check. */
export function QuizEntry({ code }: { code: string }) {
  const [step, setStep] = useState<Step>({ kind: 'loading' });
  const [quizName, setQuizName] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const quiz = await api.getQuiz(code);
        if (cancelled) return;
        setQuizName(quiz.name);

        // Host link: /q/CODE#host=KEY
        const hostKey = new URLSearchParams(location.hash.slice(1)).get('host');
        if (hostKey) {
          history.replaceState(null, '', location.pathname);
          const creds = await api.hostRejoin(code, hostKey);
          const saved = { ...creds, hostKey, name: sessions.load(code)?.name ?? 'Quizmaster' };
          sessions.save(saved);
          if (!cancelled) setStep({ kind: 'check', session: saved });
          return;
        }
        const saved = sessions.load(code);
        setStep(saved ? { kind: 'check', session: saved } : { kind: 'name' });
      } catch (err) {
        if (!cancelled) setStep({ kind: 'missing', message: err instanceof ApiError ? err.message : 'Could not load this quiz.' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (step.kind === 'loading') return <div className="center-screen">Finding the quiz…</div>;

  if (step.kind === 'missing') {
    return (
      <div className="center-screen">
        <div className="panel narrow">
          <h1>Quiz not found</h1>
          <p>{step.message}</p>
          <button className="btn" onClick={() => navigate('/')}>
            Go to the start page
          </button>
        </div>
      </div>
    );
  }

  if (step.kind === 'name') {
    return (
      <NameForm
        quizName={quizName}
        onJoined={(session) => {
          sessions.save(session);
          setStep({ kind: 'check', session });
        }}
        code={code}
      />
    );
  }

  if (step.kind === 'check') {
    return (
      <MicCheck
        quizName={quizName}
        session={step.session}
        onSwitchUser={() => {
          sessions.clear(code);
          setStep({ kind: 'name' });
        }}
        onReady={(voice) => setStep({ kind: 'room', session: step.session, voice })}
      />
    );
  }

  return (
    <RoomView
      session={step.session}
      voice={step.voice}
      onLeave={() => {
        void step.voice.stop();
        setStep({ kind: 'check', session: step.session });
      }}
      onSessionInvalid={() => {
        sessions.clear(code);
        void step.voice.stop();
        setStep({ kind: 'name' });
      }}
    />
  );
}

function NameForm({ code, quizName, onJoined }: { code: string; quizName: string; onJoined: (s: SavedSession) => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const creds = await api.join(code, name);
      onJoined({ ...creds, name: name.trim() });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not join. Try again.');
      setBusy(false);
    }
  }

  return (
    <div className="center-screen">
      <form className="panel narrow" onSubmit={submit}>
        <p className="wordmark small">Elixir</p>
        <h1>{quizName}</h1>
        <label>
          Your name
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={LIMITS.nameLength} required placeholder="What should your team call you?" />
        </label>
        <button className="btn btn-primary" disabled={busy || !name.trim()}>
          {busy ? 'Joining…' : 'Continue'}
        </button>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
      </form>
    </div>
  );
}

/** Preview the microphone, pick devices, then join. The Join click also unlocks audio playback. */
function MicCheck({
  quizName,
  session,
  onReady,
  onSwitchUser,
}: {
  quizName: string;
  session: SavedSession;
  onReady: (v: VoiceSession) => void;
  onSwitchUser: () => void;
}) {
  const prefs = useRef(loadPrefs()).current;
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([]);
  const [inputId, setInputId] = useState(prefs.inputId ?? 'default');
  const [level, setLevel] = useState(-120);
  const [micError, setMicError] = useState<string | null>(null);
  const [joinMuted, setJoinMuted] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const isHost = Boolean(session.hostKey);

  // Live mic preview with a level meter.
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicError('Microphones only work on a secure (https) page. Ask the host for the https link.');
      return;
    }
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let timer = 0;
    let cancelled = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, ...(inputId !== 'default' ? { deviceId: { exact: inputId } } : {}) },
        });
        if (cancelled) return stream.getTracks().forEach((t) => t.stop());
        setMicError(null);
        const devices = await navigator.mediaDevices.enumerateDevices();
        setInputs(devices.filter((d) => d.kind === 'audioinput' && d.deviceId));
        ctx = new AudioContext();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const buf = new Float32Array(analyser.fftSize);
        timer = window.setInterval(() => {
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (const v of buf) sum += v * v;
          setLevel(sum > 0 ? 10 * Math.log10(sum / buf.length) : -120);
        }, 80);
      } catch (err) {
        const name = (err as Error).name;
        if (name === 'OverconstrainedError' || name === 'NotFoundError') {
          if (inputId !== 'default') return setInputId('default');
          setMicError('No microphone found. Plug one in, then try again.');
        } else if (name === 'NotAllowedError') {
          setMicError('Microphone access is blocked. Click the lock icon in the address bar, allow the microphone, then try again.');
        } else {
          setMicError(`Could not open your microphone (${(err as Error).message}).`);
        }
      }
    })();
    return () => {
      cancelled = true;
      clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
      void ctx?.close();
    };
  }, [inputId, attempt]);

  async function join() {
    setJoining(true);
    setJoinError(null);
    try {
      localStorage.setItem(PREFS, JSON.stringify({ ...prefs, inputId }));
    } catch {
      /* ignore */
    }
    const voice = new VoiceSession(session, isHost, { micMuted: joinMuted, inputId, outputId: prefs.outputId });
    try {
      await voice.start(); // called inside the click, so audio playback is allowed
      onReady(voice);
    } catch (err) {
      const problem = voice.store.get().problem;
      if (err instanceof ApiError && err.status === 401) {
        onSwitchUser();
        return;
      }
      setJoinError(problem ?? 'Could not connect. Try again.');
      void voice.stop();
      setJoining(false);
    }
  }

  return (
    <div className="center-screen">
      <div className="panel narrow mic-check">
        <p className="wordmark small">Elixir</p>
        <h1>{quizName}</h1>
        <p className="who">
          Joining as <strong>{session.name || (isHost ? 'Quizmaster' : 'you')}</strong>
          {isHost && <span className="role-tag">Quizmaster</span>}
          <button className="link-btn" onClick={onSwitchUser}>
            Not you?
          </button>
        </p>

        <div className="check-row">
          <HeadphonesIcon width={28} height={28} />
          <p>Wear headphones. With speakers, your mic picks up everyone else and whispers can echo into the room.</p>
        </div>

        {micError ? (
          <div className="notice notice-bad" role="alert">
            <p>{micError}</p>
            <button className="btn" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </button>
          </div>
        ) : (
          <>
            <label>
              Microphone
              <select value={inputId} onChange={(e) => setInputId(e.target.value)}>
                <option value="default">System default</option>
                {inputs
                  .filter((d) => d.deviceId !== 'default')
                  .map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || 'Microphone'}
                    </option>
                  ))}
              </select>
            </label>
            <div className="level-row">
              <span>Say something</span>
              <LevelBar db={level} />
            </div>
          </>
        )}

        <label className="checkbox">
          <input type="checkbox" checked={joinMuted} onChange={(e) => setJoinMuted(e.target.checked)} />
          Join with my microphone muted
        </label>

        <button className="btn btn-primary btn-big" disabled={Boolean(micError) || joining} onClick={join}>
          {joining ? 'Connecting…' : 'Join the quiz'}
        </button>
        {joinError && (
          <p className="form-error" role="alert">
            {joinError}
          </p>
        )}
      </div>
    </div>
  );
}
